import { useCallback, useEffect, useState, type FunctionComponent } from "react";
import { Outlet, useSearchParams } from "react-router-dom";
import { debounce, handleHttpError } from "../../../../utils/utils";
import { useToast } from "../../../../hooks/use-toast";
import Spinner from "../../../../components/spinner.components";
import usePostStore from "../../../../store/post.store";
import type { IPost } from "../../../../types/models/post.models.types";
import { CampaignPostBrandPartial } from "./campaign-post.brand.partials";
import { useSearchPostsEphemeralHttp } from "../../../../hooks/http/ephemeral/campaigns/search-posts.ephemeral.http";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";

enum PostSortBy {
  CREATED_AT_NEWEST = "createdAtNewest",
  CREATED_AT_OLDEST = "createdAtOldest",
}

const CampaignPostSearchBrandPartial: FunctionComponent = () => {
  const [search] = useSearchParams();
  const { execute: searchPostsHttp, fetching: searching } =
    useSearchPostsEphemeralHttp();

  const { toast } = useToast();
  const { setData, data: postStore } = usePostStore();
  const [sortBy, setSortBy] = useState<PostSortBy>(PostSortBy.CREATED_AT_NEWEST);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const debouncedPostSearch = useCallback(
    debounce(async (searchValue: string, nextSortBy: PostSortBy, nextSortOrder: "asc" | "desc") => {
      let apiSortBy = "createdAt";
      let apiSortOrder: "asc" | "desc" = nextSortOrder;

      if (nextSortBy === PostSortBy.CREATED_AT_NEWEST) {
        apiSortBy = "createdAt";
        apiSortOrder = "desc";
      } else if (nextSortBy === PostSortBy.CREATED_AT_OLDEST) {
        apiSortBy = "createdAt";
        apiSortOrder = "asc";
      }

      const response = await searchPostsHttp({
        body: null,
        queryParams: {
          searchString: searchValue,
          sortBy: apiSortBy,
          sortOrder: apiSortOrder,
        },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }
      setData(response.data.posts);
    }, 500),
    [],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (search.get("query")) {
      setData([]);
      debouncedPostSearch(search.get("query") ?? "", sortBy, sortOrder);
    }
  }, [search.get("query"), sortBy, sortOrder]);

  return (
    <div className="flex flex-col w-full">
      <div className="flex justify-between items-center w-full">
        <div className="py-8 md:py-16 w-full font-semibold md:text-3xl">
          Search results:
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={sortBy}
            onValueChange={(value) => {
              const nextSortBy = value as PostSortBy;
              setData([]);
              setSortBy(nextSortBy);
              if (nextSortBy === PostSortBy.CREATED_AT_NEWEST) {
                setSortOrder("desc");
                debouncedPostSearch(search.get("query") ?? "", nextSortBy, "desc");
              } else {
                setSortOrder("asc");
                debouncedPostSearch(search.get("query") ?? "", nextSortBy, "asc");
              }
            }}
          >
            <SelectTrigger className="gap-3 bg-tertiarybuttonbg border-border rounded-lg w-fit">
              <span className="text-secondary">Sort by:</span>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={PostSortBy.CREATED_AT_NEWEST}>
                Newest first
              </SelectItem>
              <div className="border-border border-t" />
              <SelectItem value={PostSortBy.CREATED_AT_OLDEST}>
                Oldest first
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {searching ? (
        <div className="flex justify-center items-center h-96">
          <Spinner />
        </div>
      ) : (
        <div className="gap-6 grid grid-cols-1 md:grid-cols-3 w-full">
          {postStore.map((post: IPost) => (
            <CampaignPostBrandPartial key={post.id} post={post} />
          ))}
        </div>
      )}
      {!postStore.length && !searching && (
        <div className="flex justify-center items-center h-96">
          No posts found
        </div>
      )}
      <Outlet />
    </div>
  );
};

export default CampaignPostSearchBrandPartial;
